import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const NAV_LINKS = [
    { label: "Home", to: "/", id: "home" },
    { label: "About Us", to: "/about", id: "about" },
    { label: "Our Products", to: "/products", id: "products" },
    { label: "Contact Us", to: "/contact", id: "contact" },
    { label: "Our Policy", to: "/policy", id: "policy" },
];

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 w-full border-b transition-shadow duration-300 ${
                scrolled
                    ? "border-[#E2E8F4] bg-bone/85 shadow-[0_8px_30px_rgba(10,27,63,0.08)] backdrop-blur-xl"
                    : "border-transparent bg-bone/60 backdrop-blur-md"
            }`}
        >
            <div
                className="mx-auto flex max-w-7xl items-center justify-between px-6 sm:px-10"
                style={{ height: "1.5cm", minHeight: "57px" }}
                data-testid="header-bar"
            >
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex min-w-[250px] shrink-0 items-center gap-3"
                    onClick={() => setOpen(false)}
                >
                    <img
                        src="/assets/logo-emblem-dark.png"
                        alt="Coaching Crafters AI emblem"
                        width={44}
                        height={44}
                        className="h-11 w-11 object-contain"
                    />
                    <span className="whitespace-nowrap font-serif text-2xl font-bold tracking-tight text-ink">
                        Coaching Crafters <span className="text-ember">AI</span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-7 xl:flex" data-testid="desktop-nav">
                    {NAV_LINKS.map((link) => (
                        <Link
                            key={link.id}
                            to={link.to}
                            data-testid={`nav-${link.id}-link`}
                            className="text-sm font-medium text-ink/70 transition-colors duration-200 hover:text-ember"
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 xl:flex">
                    <Link
                        to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
                        data-testid="nav-login-button"
                        className="rounded-full border border-ink/15 px-4 py-1.5 text-sm font-semibold text-ink transition-colors duration-200 hover:border-ember hover:text-ember"
                    >
                        {user ? (user.role === "admin" ? "Admin" : "Account") : "Login"}
                    </Link>
                    <Link
                        to="/login?mode=signup"
                        data-testid="nav-cta-button"
                        className="rounded-full bg-ember px-4 py-1.5 text-sm font-semibold text-bone shadow-[0_8px_20px_rgba(30,79,163,0.35)] transition-colors duration-200 hover:bg-ember-dark"
                    >
                        Start free trial
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-button"
                    className="rounded-lg p-2 text-ink xl:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle navigation menu"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden border-t border-[#E2E8F4] bg-bone/95 backdrop-blur-xl xl:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {NAV_LINKS.map((link) => (
                                <Link
                                    key={link.id}
                                    to={link.to}
                                    data-testid={`mobile-nav-${link.id}-link`}
                                    onClick={() => setOpen(false)}
                                    className="rounded-lg px-3 py-3 text-base font-medium text-ink/80 transition-colors hover:bg-sand hover:text-ember"
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <div className="mt-4 flex flex-col gap-3">
                                <Link
                                    to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
                                    data-testid="mobile-nav-login-button"
                                    onClick={() => setOpen(false)}
                                    className="rounded-full border border-ink/15 px-5 py-3 text-center text-sm font-semibold text-ink"
                                >
                                    {user ? (user.role === "admin" ? "Admin" : "Account") : "Login"}
                                </Link>
                                <Link
                                    to="/login?mode=signup"
                                    data-testid="mobile-nav-cta-button"
                                    onClick={() => setOpen(false)}
                                    className="rounded-full bg-ember px-5 py-3 text-center text-sm font-semibold text-bone"
                                >
                                    Start free trial
                                </Link>
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
